import { constants, utils } from '@frontend/partviewer';
import { type PreparedViewerItem } from '@frontend/partviewer/src/utils';

import { FEATURE_IGNORE_KEY, MATERIAL_INDICATOR } from './constants';
import type { CadViewerState } from './data/reducer';

const { SPLIT_CHAR, UNITS } = constants;
const { constructFeatureComparatorString, constructFeatureDescriptorString } =
  utils;

/*
This function is used to encode the viewer state for purposes snapping to
for comments. As a result, we will only want to pass through objects
with permanence in the tech data package. Ephemeral renderings like material
widget display should not be saved into the state.
*/
/** The parts of the CAD viewer's state an encoded view keeps. */
export type EncodableCadViewerState = Pick<
  CadViewerState,
  | 'activeBodies'
  | 'enabledBodies'
  | 'expandedBodies'
  | 'explodedState'
  | 'focusedBodies'
  | 'focusedUuid'
  | 'selectedBodies'
  | 'showAdditionalKeysExplicit'
  | 'visibleBodies'
>;

export const getEncodedCADViewerState = (state: EncodableCadViewerState) => {
  const {
    activeBodies,
    enabledBodies,
    expandedBodies,
    explodedState,
    focusedBodies,
    focusedUuid,
    selectedBodies,
    showAdditionalKeysExplicit,
    visibleBodies,
  } = state;
  return {
    ab: activeBodies.filter((id) => id.indexOf(MATERIAL_INDICATOR) === -1),
    enb: enabledBodies,
    exb: expandedBodies,
    exs: explodedState,
    fb: focusedBodies.filter((id) => id.indexOf(MATERIAL_INDICATOR) === -1),
    fu: focusedUuid,
    sake: showAdditionalKeysExplicit.map((key) => {
      const pieces = key.split(SPLIT_CHAR);
      return `${pieces[1]}${SPLIT_CHAR}${pieces[2]}`;
    }),
    sb: selectedBodies,
    vb: visibleBodies.filter((id) => id.indexOf(MATERIAL_INDICATOR) === -1),
  };
};

export const decodeEncodedCADViewerState = (
  encodedState: Partial<ReturnType<typeof getEncodedCADViewerState>>,
) => {
  const {
    ab: activeBodies,
    enb: enabledBodies,
    exb: expandedBodies,
    exs: explodedState,
    fb: focusedBodies,
    fu: focusedUuid,
    sake: showAdditionalKeysExplicitInfo,
    sb: selectedBodies,
    vb: visibleBodies,
  } = encodedState;
  const out: Partial<CadViewerState> = {};
  if (activeBodies) out.activeBodies = activeBodies;
  if (enabledBodies) out.enabledBodies = enabledBodies;
  if (expandedBodies) out.expandedBodies = expandedBodies;
  if (explodedState) out.explodedState = explodedState;
  if (focusedBodies) out.focusedBodies = focusedBodies;
  if (focusedUuid) out.focusedUuid = focusedUuid;
  if (selectedBodies) out.selectedBodies = selectedBodies;
  if (visibleBodies) out.visibleBodies = visibleBodies;
  if (showAdditionalKeysExplicitInfo && focusedUuid) {
    out.showAdditionalKeysExplicit = [];
    for (const key of showAdditionalKeysExplicitInfo) {
      out.showAdditionalKeysExplicit.push(`${focusedUuid}${SPLIT_CHAR}${key}`);
    }
  }
  return out;
};

/**
 * What the feature tree needs from an item: the fields geometric features and
 * feedback items share. Tree helpers are generic over it, so a caller's
 * concrete item type flows through to its callbacks.
 */
export type FeatureTreeItem = PreparedViewerItem;

/** Grouping row. Its key carries FEATURE_IGNORE_KEY, so checking it never selects an item. */
type FeatureTreeGroupBase = {
  key: string;
  title: string;
  canSnap: false;
};

/** An item row: the prepared item itself, with the tree fields added. */
export type FeatureTreeLeafNode<T extends FeatureTreeItem> = T & {
  kind: 'leaf';
  title: string | undefined;
  children: [];
};

/** Items in one family whose displayed properties are identical. */
export type FeatureTreePropertyGroupNode<T extends FeatureTreeItem> =
  FeatureTreeGroupBase & {
    kind: 'properties';
    color: T['color'];
    children: FeatureTreeLeafNode<T>[];
  };

export type FeatureTreeFamilyNode<T extends FeatureTreeItem> =
  FeatureTreeGroupBase & {
    kind: 'family';
    /** null when the family's items differ in colour. */
    color: T['color'] | null;
    /** First item in the family: source of its message and (for feedback) level. */
    feature: T;
    message: string | undefined;
    children: (FeatureTreePropertyGroupNode<T> | FeatureTreeLeafNode<T>)[];
  };

export type FeatureTreeNode<T extends FeatureTreeItem> =
  | FeatureTreeFamilyNode<T>
  | FeatureTreePropertyGroupNode<T>
  | FeatureTreeLeafNode<T>;

export const groupFeaturesByFamily = <T extends FeatureTreeItem>(
  features: T[],
) => {
  const featuresByFamily = new Map<
    string,
    { family: string; feature: T; children: T[] }
  >();
  for (const feature of features) {
    // Missing family still shares a group.
    const family = feature.family ?? '';
    const group = featuresByFamily.get(family);
    if (group) {
      group.children.push(feature);
    } else {
      featuresByFamily.set(family, { family, feature, children: [feature] });
    }
  }
  return Array.from(featuresByFamily.values()).sort((a, b) => {
    const _a = a.family.toLowerCase();
    const _b = b.family.toLowerCase();
    return _a < _b ? -1 : _a > _b ? 1 : 0;
  });
};

const groupFeaturesByProperties = <T extends FeatureTreeItem>(
  features: T[],
  displayUnits: (typeof UNITS)[keyof typeof UNITS],
  nativeUnits: (typeof UNITS)[keyof typeof UNITS],
  dimensionalPrecision: number,
  displayScale: number,
) => {
  const featuresByComparator = new Map<
    string,
    {
      feature: T;
      children: T[];
      comparator: string;
      sortObject: Record<string, number>;
    }
  >();
  for (const feature of features) {
    const comparator = constructFeatureComparatorString(
      feature,
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
    );
    // An empty comparator still belongs in the tree.
    const comparatorKey = comparator ?? '';
    const group = featuresByComparator.get(comparatorKey);
    if (group) {
      group.children.push(feature);
    } else {
      const numericEntries = feature.dbData.flatMap(({ name, value }) =>
        typeof value === 'number' ? ([[name, value]] as const) : [],
      );
      featuresByComparator.set(comparatorKey, {
        feature,
        children: [feature],
        comparator: comparatorKey,
        sortObject: Object.fromEntries(numericEntries),
      });
    }
  }

  return Array.from(featuresByComparator.values()).sort((a, b) => {
    //sort by these sortObject.props (in descending order)
    for (const prop of ['volume', 'area', 'length', 'radius', 'cut_length']) {
      const _aValue = a.sortObject[prop];
      const _bValue = b.sortObject[prop];
      if (_aValue > _bValue) {
        return -1;
      } else if (_aValue < _bValue) {
        return 1;
      }
      //otherwise continue to compare next prop
    }
    return 0; //equal
  });
};

/*
Will group either geometric features or manufacturability feedback into form
for antd tree component. Initially grouped by family, then grouped by properties
for second level.
*/
export const getFeatureTreeData = <T extends FeatureTreeItem>(
  features: T[] = [],
  displayUnits: (typeof UNITS)[keyof typeof UNITS] = UNITS.IN,
  nativeUnits: (typeof UNITS)[keyof typeof UNITS] = UNITS.MM,
  dimensionalPrecision = 3,
  displayScale = 1.0,
): FeatureTreeFamilyNode<T>[] => {
  const familyGroups = groupFeaturesByFamily(features);
  const data: FeatureTreeFamilyNode<T>[] = [];
  for (const familyGroup of familyGroups) {
    const color = familyGroup.feature.color;
    const isUniformColor = !familyGroup.children.some((f) => f.color !== color);
    const familyEntry: FeatureTreeFamilyNode<T> = {
      kind: 'family',
      key: familyGroup.family + FEATURE_IGNORE_KEY,
      title: `${familyGroup.family} (${familyGroup.children.length})`,
      color: isUniformColor ? color : null,
      children: [],
      feature: familyGroup.feature,
      message: familyGroup.feature.message,
      canSnap: false,
    };

    const propertiesGroups = groupFeaturesByProperties(
      familyGroup.children,
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
    );
    for (const propertiesGroup of propertiesGroups) {
      const dataString = constructFeatureDescriptorString(
        propertiesGroup.feature,
        displayUnits,
        nativeUnits,
        dimensionalPrecision,
        displayScale,
      );
      if (propertiesGroup.children.length > 1) {
        const propsEntry: FeatureTreePropertyGroupNode<T> = {
          kind: 'properties',
          key: propertiesGroup.feature.key + FEATURE_IGNORE_KEY,
          title: `${
            dataString ? dataString : propertiesGroup.feature.family
          } (${propertiesGroup.children.length})`,
          color: propertiesGroup.feature.color,
          children: [],
          canSnap: false,
        };
        for (const feature of propertiesGroup.children) {
          propsEntry.children.push({
            ...feature,
            kind: 'leaf',
            title: feature.family,
            children: [],
          });
        }
        familyEntry.children.push(propsEntry);
      } else {
        familyEntry.children.push({
          ...propertiesGroup.feature,
          kind: 'leaf',
          title: dataString ? dataString : propertiesGroup.feature.family,
          children: [],
        });
      }
    }
    data.push(familyEntry);
  }
  return data;
};
